import { useState, useEffect } from 'react';
import api from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { toast } from 'sonner';
import { Crown, Check, Loader2 } from 'lucide-react';

export function SubscribeModal({ open, onOpenChange, creator }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    if (open && creator?.id) {
      const fetchTiers = async () => {
        try {
          const response = await api.get(`/subscriptions/tiers/${creator.id}`);
          setTiers(response.data);
        } catch (error) {
          console.error('Error fetching tiers:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchTiers();
    }
  }, [open, creator?.id]);

  const handleSubscribe = async (tier) => {
    setSubscribing(tier.id);
    try {
      const response = await api.post('/subscriptions/subscribe', {
        tier_id: tier.id,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
      setSubscribing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="subscribe-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Subscribe to {creator?.display_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Creator Info */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl">
            <Avatar className="w-12 h-12">
              <AvatarImage src={creator?.avatar} />
              <AvatarFallback>{creator?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{creator?.display_name}</p>
              <p className="text-sm text-muted-foreground">Support and get exclusive perks</p>
            </div>
          </div>

          {/* Tiers */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-8">
              <Crown className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                This creator hasn't set up subscriptions yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {tiers.map((tier) => (
                <div 
                  key={tier.id}
                  className="p-4 rounded-xl border border-border hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-lg">{tier.name}</h3>
                      <p className="text-sm text-muted-foreground">{tier.description}</p>
                    </div>
                    <p className="text-2xl font-bold text-primary">
                      ${tier.price}<span className="text-sm text-muted-foreground">/mo</span>
                    </p>
                  </div>
                  
                  {tier.benefits?.length > 0 && (
                    <ul className="space-y-1 mb-4">
                      {tier.benefits.map((benefit, i) => (
                        <li key={i} className="text-sm flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <Button
                    onClick={() => handleSubscribe(tier)}
                    disabled={subscribing === tier.id}
                    className="w-full rounded-full"
                    data-testid={`subscribe-tier-${tier.id}`}
                  >
                    {subscribing === tier.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Subscribe for ${tier.price}/month
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-center text-muted-foreground">
            Cancel anytime. Payments processed securely by Stripe.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SubscribeModal;
